import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import ExcelJS from 'exceljs';
import styles from "../../styles/CampusStyles/PantallaDeCampus.module.css";
import cargosStyles from "../../styles/GeneracionCargosManualesStyles/GeneracionCargosManuales.module.css";
import fc from "./FirmaConcetrados.module.css";
import { getEstudiantesGrupo, guardarRubrica, getInfoAsesorUsuario } from "../../api/calificaciones";
import calificacionesStyles from "../RegistroCalificacionesScreen/RegistroCalificaciones.module.css";
import Sidebar from "../../components/Sidebar";
import HeaderGlobal from "../../components/HeaderGlobal";
import FirmaConcetradosGruposScreen from "./FirmaConcetradosGruposScreen";

const PARCIALES = [
  { key: 'primer', label: 'Primer Parcial' },
  { key: 'segundo', label: 'Segundo Parcial' },
  { key: 'tercer', label: 'Tercer Parcial' },
];



function getSemanaLabels(parcialIdx) {
  if (parcialIdx === 1) {
    // Segundo Parcial: 5 semanas, actividades [0 - 10]
    return [
      { label: 'Semana 5', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 6', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 7', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 8', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 9', actividad: '[0 - 10]', max: 10 },
    ];
  }
  if (parcialIdx === 2) {
    // Tercer Parcial: 5 semanas, actividades [0 - 10]
    return [
      { label: 'Semana 10', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 11', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 12', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 13', actividad: '[0 - 10]', max: 10 },
      { label: 'Semana 14', actividad: '[0 - 10]', max: 10 },
    ];
  }
  // Primer parcial (default): 4 semanas
  return [
    { label: 'Semana 1', actividad: '[0 - 12]', max: 12 },
    { label: 'Semana 2', actividad: '[0 - 12]', max: 12 },
    { label: 'Semana 3', actividad: '[0 - 13]', max: 13 },
    { label: 'Semana 4', actividad: '[0 - 13]', max: 13 },
  ];
}

// Función para redondear el promedio según la escala dada (0 a 10)
function redondearPromedio(valor) {
  if (valor === 0) return 0;
  if (valor > 0 && valor < 5.9) return 5;
  if (valor >= 5.9 && valor < 6.9) return 6;
  if (valor >= 6.9 && valor < 7.5) return 7;
  if (valor >= 7.5 && valor < 8.5) return 8;
  if (valor >= 8.5 && valor < 9.5) return 9;
  if (valor >= 9.5 && valor <= 10) return 10;
  return 0; // Si está fuera de rango, regresa 0
}


const ConcentradoTable = React.forwardRef(function ConcentradoTable({ parcialIdx, busqueda, idGrupo, idCicloEscolar, grupoInfo }, ref) {
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardandoIdx, setGuardandoIdx] = useState(null);
  const [guardadoIdx, setGuardadoIdx] = useState(null);
  const debounceTimers = useRef({});
  const titulo = `CALIFICACIÓN ${PARCIALES[parcialIdx].label.toUpperCase()}`;
  const periodo = '2026-1';
  const semanas = getSemanaLabels(parcialIdx);

  // Cargar estudiantes reales al montar o cambiar grupo
  useEffect(() => {
    if (!idGrupo) return;
    setLoading(true);
    getEstudiantesGrupo(idGrupo)
      .then(data => {
        // data.estudiantes || [] para compatibilidad con API
        const raw = data.estudiantes || data || [];
        // Calcular calificacionFinal para cada alumno con datos ya cargados
        const procesados = raw.map(alumno => {
          const parciales = Array.isArray(alumno.parciales) ? alumno.parciales : [];
          const totalesRedondeados = parciales.map(p => parseFloat(p?.totalRedondeado) || 0);
          const parcialesConDatos = parciales.filter(p => p?.totalRedondeado !== undefined && p?.totalRedondeado !== '');
          const promedio = parcialesConDatos.length > 0
            ? totalesRedondeados.reduce((acc, v) => acc + v, 0) / 3
            : 0;
          return { ...alumno, calificacionFinal: redondearPromedio(promedio) };
        });
        setAlumnos(procesados);
      })
      .catch(() => setAlumnos([]))
      .finally(() => setLoading(false));
  }, [idGrupo]);

  // Obtiene el valor máximo permitido para cada campo editable
  const getMaxForField = (field) => {
    const semanaFields = semanas.map((s, si) => ({ key: `semana${si + 1}`, max: s.max }));
    const semana = semanaFields.find(s => s.key === field);
    if (semana) return semana.max;
    if (field === 'coevaluacion') return 2.5;
    if (field === 'autoevaluacion') return 2.5;
    if (field === 'proyectoExamen') return 45;
    return null;
  };

  // Handler para editar cualquier campo numérico excepto "actividades", "totalSin" y "totalRedondeado"
  const handleInputChange = (alumnoIdx, field, value) => {
    setAlumnos(prev => {
      const updated = prev.map((a, idx) => {
        if (idx !== alumnoIdx) return a;
        // Clonar alumno y asegurar que parciales exista con 3 entradas
        const parciales = Array.isArray(a.parciales) ? a.parciales.map(p => ({ ...p })) : [{}, {}, {}];
        // Asegurar que el índice del parcial exista
        if (!parciales[parcialIdx]) parciales[parcialIdx] = {};
        const d = { ...parciales[parcialIdx] };
        let numValue = value === '' ? '' : parseFloat(value);
        if (numValue !== '') {
          const maxVal = getMaxForField(field);
          if (numValue < 0) numValue = 0;
          if (maxVal !== null && numValue > maxVal) numValue = maxVal;
        }
        d[field] = numValue;
        // Recalcular actividades automáticamente
        const semanaFields = semanas.map((_, si) => `semana${si + 1}`);
        if (semanaFields.includes(field)) {
          d.actividades = semanaFields.reduce((acc, f) => acc + (parseFloat(d[f]) || 0), 0);
        }
        // Recalcular totalSin y totalRedondeado automáticamente
        d.totalSin =
          (parseFloat(d.actividades) || 0) +
          (parseFloat(d.coevaluacion) || 0) +
          (parseFloat(d.autoevaluacion) || 0) +
          (parseFloat(d.proyectoExamen) || 0);
        d.totalRedondeado = redondearPromedio(d.totalSin / 10);
        parciales[parcialIdx] = d;
        // Calificación final = promedio de los totalRedondeado de los 3 parciales
        const totalesRedondeados = parciales.map(p => parseFloat(p?.totalRedondeado) || 0);
        const parcialesConDatos = parciales.filter(p => p?.totalRedondeado !== undefined && p?.totalRedondeado !== '');
        const promedio = parcialesConDatos.length > 0
          ? totalesRedondeados.reduce((acc, v) => acc + v, 0) / 3
          : 0;
        const calificacionFinal = redondearPromedio(promedio);
        return { ...a, parciales, calificacionFinal };
      });
      return updated;
    });
    // Auto-guardado con debounce de 800ms por alumno (clave incluye parcialIdx para no cancelar guardados de otro parcial)
    const key = `${parcialIdx}-${alumnoIdx}`;
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => {
      setAlumnos(prev => {
        const alumno = prev[alumnoIdx];
        if (!alumno) return prev;
        const d = (alumno.parciales || [])[parcialIdx] || {};
        const payload = {
          id_estudiante: alumno.id_estudiante || alumno.id,
          id_grupo: idGrupo,
          id_ciclo_escolar: idCicloEscolar,
          numero_parcial: parcialIdx + 1,
          actividad_s1: d.semana1 ?? null,
          actividad_s2: d.semana2 ?? null,
          actividad_s3: d.semana3 ?? null,
          actividad_s4: d.semana4 ?? null,
          actividad_s5: d.semana5 ?? null,
          cal_actividades: d.actividades ?? null,
          cal_coevaluacion: d.coevaluacion ?? null,
          cal_autoevaluacion: d.autoevaluacion ?? null,
          cal_proyecto_examen: d.proyectoExamen ?? null,
          calificacion_parcial: d.totalRedondeado || null,
          activo: 1,
        };
        setGuardandoIdx(alumnoIdx);
        guardarRubrica(payload)
          .then(() => {
            setGuardandoIdx(null);
            setGuardadoIdx(alumnoIdx);
            setTimeout(() => setGuardadoIdx(null), 2000);
          })
          .catch(() => setGuardandoIdx(null));
        return prev;
      });
    }, 800);
  };

  // Guardar manual de un alumno
  const handleGuardarManual = (alumnoIdx) => {
    const alumno = alumnos[alumnoIdx];
    if (!alumno) return;
    const d = (alumno.parciales || [])[parcialIdx] || {};
    const payload = {
      id_estudiante: alumno.id_estudiante || alumno.id,
      id_grupo: idGrupo,
      id_ciclo_escolar: idCicloEscolar,
      numero_parcial: parcialIdx + 1,
      actividad_s1: d.semana1 ?? null,
      actividad_s2: d.semana2 ?? null,
      actividad_s3: d.semana3 ?? null,
      actividad_s4: d.semana4 ?? null,
      actividad_s5: d.semana5 ?? null,
      cal_actividades: d.actividades ?? null,
      cal_coevaluacion: d.coevaluacion ?? null,
      cal_autoevaluacion: d.autoevaluacion ?? null,
      cal_proyecto_examen: d.proyectoExamen ?? null,
      calificacion_parcial: d.totalRedondeado || null,
      activo: 1,
    };
    setGuardandoIdx(alumnoIdx);
    guardarRubrica(payload)
      .then(() => {
        setGuardandoIdx(null);
        setGuardadoIdx(alumnoIdx);
        setTimeout(() => setGuardadoIdx(null), 2000);
      })
      .catch(() => setGuardandoIdx(null));
  };


  // Filtrar alumnos por búsqueda
  const alumnosFiltrados = alumnos
    .filter(alumno => {
      const texto = `${alumno.nombre || alumno.estudiante_nombre || ''} ${alumno.matricula || alumno.estudiante_matricula || ''}`.toLowerCase();
      return texto.includes((busqueda || "").toLowerCase());
    })
    .sort((a, b) => {
      const nombreA = (a.nombre || a.estudiante_nombre || '').trim();
      const nombreB = (b.nombre || b.estudiante_nombre || '').trim();
      return nombreA.localeCompare(nombreB, 'es', { sensitivity: 'base' });
    });

  // Exportar a Excel con formato institucional
  const handleDescargarExcel = async () => {
    const asesorNombre = grupoInfo?.asesor_nombre || '';
    const asignatura = grupoInfo?.nombre_materia || grupoInfo?.materia || grupoInfo?.nombre || '';
    const ciclo = grupoInfo?.ciclo || '';

    const parcialesExport = PARCIALES.map((p, idx) => ({
      ...p,
      semanas: getSemanaLabels(idx),
    }));

    // Columnas dinámicas: 3 fijas + (semanas + 6) por parcial + 1 calificación final
    const colsPerParcial = parcialesExport.map(p => p.semanas.length + 6);
    const colFinParciales = 3 + colsPerParcial.reduce((a, b) => a + b, 0);
    const colCalificacionFinal = colFinParciales + 1;
    const totalCols = colCalificacionFinal;
    const fila1 = Array(totalCols).fill('');
    const fila2 = Array(totalCols).fill('');
    const fila3 = Array(totalCols).fill('');

    fila1[0] = `Asesor Académico: ${asesorNombre}`;
    fila2[0] = `Asignatura: ${asignatura}`;
    fila2[1] = '';
    fila2[2] = ciclo;

    fila3[0] = 'N°';
    fila3[1] = 'Nombre completo';
    fila3[2] = 'Matrícula';
    fila3[colCalificacionFinal - 1] = 'Calificación\nfinal';

    let filasBase = 3;
    parcialesExport.forEach((parcial) => {
      const sc = parcial.semanas.length;
      parcial.semanas.forEach((s, si) => { fila1[filasBase + si] = s.label; });
      fila1[filasBase + sc] = `CALIFICACIÓN ${parcial.label.toUpperCase()}`;
      parcial.semanas.forEach((s, si) => { fila3[filasBase + si] = `Actividad\n${s.actividad}`; });
      fila3[filasBase + sc]     = 'Actividades';
      fila3[filasBase + sc + 1] = 'Coevaluación\n[0 - 2.5]';
      fila3[filasBase + sc + 2] = 'Autoevaluación\n[0 - 2.5]';
      fila3[filasBase + sc + 3] = 'Proyecto /\nExamen\n[0 - 45]';
      fila3[filasBase + sc + 4] = 'TOTAL\nSin redondear';
      fila3[filasBase + sc + 5] = 'TOTAL\nRedondeado';
      filasBase += sc + 6;
    });

    const filasDatos = alumnosFiltrados.map((alumno, i) => {
      const fila = [
        i + 1,
        alumno.nombre || alumno.estudiante_nombre || '',
        alumno.matricula || alumno.estudiante_matricula || '',
      ];

      parcialesExport.forEach((parcial, idxParcial) => {
        const datos = (alumno.parciales || [])[idxParcial] || {};
        parcial.semanas.forEach((_, si) => fila.push(datos[`semana${si + 1}`] ?? ''));
        fila.push(
          datos.actividades ?? '',
          datos.coevaluacion ?? '',
          datos.autoevaluacion ?? '',
          datos.proyectoExamen ?? '',
          datos.totalSin ?? '',
          datos.totalRedondeado ?? '',
        );
      });

      fila.push(alumno.calificacionFinal ?? '');

      return fila;
    });

    const aoa = [fila1, fila2, fila3, ...filasDatos];

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Concentrado');

    aoa.forEach((row) => ws.addRow(row));

    // Celdas combinadas
    ws.mergeCells('A1:C1');
    ws.mergeCells('A2:B2');
    let mergeBase = 3;
    parcialesExport.forEach((parcial) => {
      const sc = parcial.semanas.length;
      // El título del parcial ocupa las 6 cols de datos (actividades..totalRedondeado)
      ws.mergeCells(1, mergeBase + sc + 1, 1, mergeBase + sc + 6);
      mergeBase += sc + 6;
    });

    // Anchos de columna
    const widths = [
      { width: 4 },
      { width: 30 },
      { width: 12 },
    ];
    parcialesExport.forEach((parcial) => {
      parcial.semanas.forEach(() => widths.push({ width: 13 }));
      widths.push(
        { width: 13 },
        { width: 14 },
        { width: 15 },
        { width: 13 },
        { width: 17 },
        { width: 15 },
      );
    });
    widths.push({ width: 15 });
    ws.columns = widths;

    ws.getRow(1).height = 22;
    ws.getRow(2).height = 20;
    ws.getRow(3).height = 34;

    const COLOR_GRIS_META = 'FFE7E6E6';
    const COLOR_AZUL_BLOQUE = 'FF2F75B5';
    const COLOR_AZUL_ENCABEZADO = 'FF5B9BD5';
    const COLOR_AZUL_DATOS = 'FFDCE6F1';

    const borderAll = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };

    const applyStyle = (cell, {
      bold = false,
      size,
      fill,
      align = 'center',
      wrap = false,
      numFmt,
    }) => {
      cell.font = { bold, size, color: { argb: 'FF000000' } };
      if (fill) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fill },
        };
      }
      cell.alignment = { horizontal: align, vertical: 'middle', wrapText: wrap };
      cell.border = borderAll;
      if (numFmt) cell.numFmt = numFmt;
    };

    // Mapa de metadatos por columna Excel (1-based) para estilos dinámicos
    const colMeta = {};
    let colMetaOffset = 4;
    parcialesExport.forEach((parcial) => {
      const sc = parcial.semanas.length;
      for (let i = 0; i < sc + 6; i++) {
        colMeta[colMetaOffset + i] = { pos: i, semanasCount: sc };
      }
      colMetaOffset += sc + 6;
    });

    const lastRow = ws.rowCount;
    const lastCol = totalCols;

    for (let r = 1; r <= lastRow; r += 1) {
      for (let c = 1; c <= lastCol; c += 1) {
        const cell = ws.getCell(r, c);
        const esBloqueParcial = c >= 4 && c <= colFinParciales;
        const esColCalificacionFinal = c === colCalificacionFinal;
        const meta = colMeta[c] || { pos: -1, semanasCount: 4 };
        const posEnBloque = esBloqueParcial ? meta.pos : -1;

        if (r === 1 && c <= 3) {
          applyStyle(cell, { bold: true, fill: COLOR_GRIS_META, align: 'left', wrap: true });
        } else if (r === 2 && c <= 3) {
          applyStyle(cell, { bold: true, fill: COLOR_GRIS_META, align: c === 3 ? 'center' : 'left', wrap: true });
        } else if (r === 1 && esBloqueParcial && posEnBloque >= 0 && posEnBloque < meta.semanasCount) {
          applyStyle(cell, { bold: true, fill: COLOR_AZUL_BLOQUE });
        } else if (r === 1 && esBloqueParcial && posEnBloque >= meta.semanasCount) {
          applyStyle(cell, { bold: true, size: 13, fill: COLOR_AZUL_BLOQUE });
        } else if (r === 3) {
          applyStyle(cell, { bold: true, fill: COLOR_AZUL_ENCABEZADO, wrap: true });
        } else {
          if (c === 2) applyStyle(cell, { align: 'left' });
          else if (c === 3) applyStyle(cell, { align: 'left', fill: COLOR_AZUL_DATOS, numFmt: '@' });
          else if (esColCalificacionFinal) applyStyle(cell, { fill: COLOR_AZUL_DATOS });
          else if (esBloqueParcial) applyStyle(cell, { fill: COLOR_AZUL_DATOS });
          else applyStyle(cell, {});
        }
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const cleanFilePart = (value) =>
      String(value || '')
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const fileAsesor = cleanFilePart(asesorNombre) || 'SinAsesor';
    const fileMateria = cleanFilePart(asignatura) || 'SinMateria';
    const fileCiclo = cleanFilePart(ciclo) || 'SinCiclo';
    link.download = `Rubrica ${fileAsesor} ${fileMateria} ${fileCiclo}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Exponer descargarExcel al padre
  useImperativeHandle(ref, () => ({ descargarExcel: handleDescargarExcel, tieneAlumnos: alumnosFiltrados.length > 0 }), [alumnosFiltrados, parcialIdx]);

  if (loading) {
    return <div className={fc.tableWrapper}><div style={{padding: 32, textAlign: 'center', color: '#888'}}>Cargando estudiantes...</div></div>;
  }
  return (
    <div className={fc.tableWrapper}>
      <div style={{
        background: '#002f6c',
        color: '#fff',
        padding: '12px 16px',
        marginBottom: '16px',
        fontSize: '14px',
        fontWeight: '600',
        borderRadius: '4px'
      }}>
        Asesor Académico: <strong>{grupoInfo?.asesor_nombre || 'Sin asesor'}</strong>
      </div>
      <table className={fc.concentradoTable}>
        <thead>
          {/* Fila principal: agrupa semanas y título del parcial */}
          <tr className={fc.tableHeader}>
            <th rowSpan={2} style={{ textAlign: 'left', minWidth: 160 }}>Nombre completo</th>
            <th rowSpan={2}>Matrícula</th>
            <th colSpan={semanas.length}>{semanas.map(s => s.label).join(' | ')}</th>
            <th colSpan={6}>{titulo}</th>
            <th rowSpan={2} style={{ background: '#1565c0', color: '#fff', minWidth: 90 }}>Calificación final</th>
            <th rowSpan={2} style={{ minWidth: 80 }}>Acción</th>
          </tr>
          {/* Sub-cabeceras de columnas */}
          <tr className={fc.tableSubheader}>
            {semanas.map((s, idx) => (
              <th key={idx}>Actividad<br />{s.actividad}</th>
            ))}
            <th>Actividades</th>
            <th>Coevaluación<br />[0 - 2.5]</th>
            <th>Autoevaluación<br />[0 - 2.5]</th>
            <th>Proyecto /<br />Examen<br />[0 - 45]</th>
            <th>TOTAL<br />Sin redondear</th>
            <th>TOTAL<br />Redondeado</th>
          </tr>
        </thead>
        <tbody>
          {alumnosFiltrados.length === 0 ? (
            <tr><td colSpan={14} style={{textAlign:'center',padding:32,color:'#888'}}>No hay estudiantes en este grupo.</td></tr>
          ) : (
            alumnosFiltrados.map((alumno, i) => {
              const parciales = alumno.parciales || [];
              const datos = parciales[parcialIdx] || {};
              // Buscar el índice real del alumno en el array original
              const idxReal = alumnos.findIndex(a => (a.id || a.matricula || a.estudiante_matricula) === (alumno.id || alumno.matricula || alumno.estudiante_matricula));
              const guardando = guardandoIdx === idxReal;
              const guardado = guardadoIdx === idxReal;
              return (
                <tr key={alumno.id || i} className={fc.tableRow}>
                  <td className={fc.nameCell}>{alumno.nombre || alumno.estudiante_nombre}</td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>{alumno.matricula || alumno.estudiante_matricula}</td>
                  {/* Semanas (actividades individuales) */}
                  {semanas.map((s, idx) => (
                    <td key={idx} style={{ textAlign: 'center' }}>
                      <input
                        type="number"
                        min={0}
                        max={s.max}
                        value={datos[`semana${idx + 1}`] ?? ''}
                        onChange={e => handleInputChange(idxReal, `semana${idx + 1}`, e.target.value)}
                        style={{ width: 60, textAlign: 'center' }}
                      />
                    </td>
                  ))}
                  {/* Actividades (suma de semanas, solo lectura) */}
                  <td style={{ textAlign: 'center', background: '#f5f5f5' }}>
                    <input
                      type="number"
                      value={datos.actividades ?? ''}
                      readOnly
                      style={{ width: 60, textAlign: 'center', background: '#f5f5f5', border: 'none' }}
                    />
                  </td>
                  {/* Coevaluación */}
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="number"
                      min={0}
                      max={2.5}
                      value={datos.coevaluacion ?? ''}
                      onChange={e => handleInputChange(idxReal, 'coevaluacion', e.target.value)}
                      style={{ width: 60, textAlign: 'center' }}
                    />
                  </td>
                  {/* Autoevaluación */}
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="number"
                      min={0}
                      max={2.5}
                      value={datos.autoevaluacion ?? ''}
                      onChange={e => handleInputChange(idxReal, 'autoevaluacion', e.target.value)}
                      style={{ width: 60, textAlign: 'center' }}
                    />
                  </td>
                  {/* Proyecto / Examen */}
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="number"
                      min={0}
                      max={45}
                      value={datos.proyectoExamen ?? ''}
                      onChange={e => handleInputChange(idxReal, 'proyectoExamen', e.target.value)}
                      style={{ width: 60, textAlign: 'center' }}
                    />
                  </td>
                  {/* TOTAL Sin redondear (solo lectura) */}
                  <td style={{ textAlign: 'center', background: '#f5f5f5' }}>
                    <input
                      type="number"
                      value={datos.totalSin ?? ''}
                      readOnly
                      style={{ width: 60, textAlign: 'center', background: '#f5f5f5', border: 'none' }}
                    />
                  </td>
                  {/* TOTAL Redondeado (solo lectura) */}
                  <td style={{ textAlign: 'center', background: '#f5f5f5' }}>
                    <input
                      type="number"
                      value={datos.totalRedondeado ?? ''}
                      readOnly
                      style={{ width: 60, textAlign: 'center', background: '#f5f5f5', border: 'none' }}
                    />
                  </td>
                  {/* Calificación final = promedio de los 3 parciales redondeado */}
                  <td style={{ textAlign: 'center', background: '#e3f2fd' }}>
                    <input
                      type="number"
                      value={alumno.calificacionFinal ?? ''}
                      readOnly
                      style={{ width: 60, textAlign: 'center', background: '#e3f2fd', border: 'none' }}
                    />
                  </td>
                  {/* Botón guardar manual */}
                  <td style={{ textAlign: 'center', minWidth: 80 }}>
                    <button
                      onClick={() => handleGuardarManual(idxReal)}
                      disabled={guardando}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 4,
                        border: 'none',
                        background: guardado ? '#4caf50' : '#1565c0',
                        color: '#fff',
                        fontSize: 12,
                        cursor: guardando ? 'not-allowed' : 'pointer',
                        opacity: guardando ? 0.7 : 1,
                      }}
                    >
                      {guardando ? '...' : guardado ? '✓' : 'Guardar'}
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
});


function FirmaConcetradosScreen() {
  const concentradoRef = useRef(null);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  const [tabActiva, setTabActiva] = useState(0);
  const [busqueda, setBusqueda] = useState("");

  if (!grupoSeleccionado) {
    // Mostrar pantalla de grupos
    return <FirmaConcetradosGruposScreen onSeleccionarGrupo={setGrupoSeleccionado} />;
  }

  // Mostrar pantalla de rúbrica para el grupo seleccionado
  return (
    <main className={styles.campusScreen} style={{ height: '100vh', minHeight: 'unset', overflow: 'hidden' }}>
      <div className={styles.mainLayout} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <aside className={styles.sidebarColumn} style={{ flexShrink: 0, overflow: 'hidden' }}>
          <Sidebar />
        </aside>
        <div className={styles.contentColumn} style={{ minHeight: 0, overflow: 'hidden' }}>
          <div className={styles.contentWrapper} style={{ minHeight: 0, overflow: 'hidden' }}>
            <HeaderGlobal title={"Rúbrica"} />
            <section className={styles.mainContent}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {/* Buscador de estudiante (sin ciclo) - mismo diseño que grupos, ahora con botón de regreso adentro */}
                <div className={calificacionesStyles.busquedaCard} style={{ margin: '0 32px 24px 32px', width: 'auto' }}>
                  <div className={calificacionesStyles.busquedaHeader}>
                    <button
                      className={cargosStyles.backButton}
                      style={{ marginRight: 16, marginBottom: 0 }}
                      onClick={() => setGrupoSeleccionado(null)}
                    >
                      <span style={{ fontSize: 18, marginRight: 4 }}>←</span> Regresar
                    </button>
                    <div className={calificacionesStyles.busquedaIcono}>
                      <span role="img" aria-label="Buscar">🔍</span>
                    </div>
                    <h2 className={calificacionesStyles.busquedaTitulo}>Buscar</h2>
                  </div>
                  <div className={calificacionesStyles.searchBarContainer}>
                    <div className={calificacionesStyles.searchInputWrapper} style={{ flex: 1 }}>
                      <svg className={calificacionesStyles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                      <input
                        type="text"
                        className={calificacionesStyles.searchInput}
                        placeholder="Buscar estudiante o matrícula..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className={fc.tabsContainer} style={{ margin: 0 }}>
                    {PARCIALES.map((p, idx) => (
                      <button
                        key={p.key}
                        className={`${fc.tab} ${tabActiva === idx ? fc.tabActive : ''}`}
                        onClick={() => setTabActiva(idx)}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => concentradoRef.current?.descargarExcel()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 18px',
                      borderRadius: 6,
                      border: 'none',
                      background: '#1e7e34',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Descargar Excel
                  </button>
                </div>
                <ConcentradoTable ref={concentradoRef} parcialIdx={tabActiva} busqueda={busqueda} idGrupo={grupoSeleccionado?.id_grupo} idCicloEscolar={grupoSeleccionado?.id_ciclo_escolar} grupoInfo={grupoSeleccionado} />
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}

export default FirmaConcetradosScreen;
